import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Card, Chip, DChip, DataTable, EmptyState, Modal, Pane, PaneHead, PhoneCards, PhoneScreen, PickerChip, TabBar,
} from '@/components/kit';
import type { TableRow } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useAuthStore } from '@/stores/useAuthStore';
import { Permission } from '@/types/user';
import {
  dayMonth,
  expiryLabel,
  useBranches,
  useShelf,
  useTransferActions,
  useTransfers,
  type ShelfItem,
  type Transfer,
  type TransferLine,
} from './lib/useStock';
import { StockMenu } from './StockMenu';

/**
 * Stock · Transfers — `g_tr_out` (f7), `g_tr_in` (f7b), `g_tr_hist` (f7c).
 * BALANCE THE BRANCHES.
 *
 * OUTGOING AND INCOMING ARE THE SAME ROW READ FROM TWO ENDS, which is why
 * they are two tabs and not two lists. The server decides which is which from
 * the branch you are looking from.
 *
 * IN TRANSIT IS ON NEITHER SHELF. Sending takes the boxes off the sending
 * branch because they are in a car; they arrive at the far end only when its
 * own stock manager confirms them, line by line. That middle state is the
 * point of the whole screen.
 *
 * LATER-EXPIRY BATCHES TRAVEL FIRST — the opposite of FEFO, deliberately. The
 * shortest-dated box is the one the sending branch must open next; putting it
 * in a car for an afternoon is how it expires in transit. So the earliest is
 * kept home and the long-dated stock moves.
 *
 * A SHORT LINE NEEDS A NOTE, and it is logged on both sides: the sender's
 * shelf already went down by what left, so a difference nobody explains is two
 * branches disagreeing about the same boxes with nothing to reconcile them.
 */

type Tab = 'out' | 'in' | 'history';

const NONE: Transfer[] = [];

export function StockTransfersPage() {
  const roleCheck = useAuthStore((s) => s.roleCheck);
  const canWrite = roleCheck.hasPermission(Permission.STOCK_WRITE);

  const { data: branches } = useBranches();
  const here = branches?.find((b) => b.isDefault) ?? branches?.[0] ?? null;
  const elsewhere = (branches ?? []).filter((b) => b.id !== here?.id);

  const { data, isLoading, isError } = useTransfers(here?.id ?? null);
  const transfers = data ?? NONE;
  const { data: shelf } = useShelf();
  const act = useTransferActions();

  const [tab, setTab] = useState<Tab>('out');
  const [query, setQuery] = useState('');
  const [pickedId, setPicked] = useState<string | null>(null);
  const [composing, setComposing] = useState<Transfer | null>(null);
  const [confirming, setConfirming] = useState<Transfer | null>(null);
  const go = useNavigate();
  const [params, setParams] = useSearchParams();
  const [dest, setDest] = useState('');

  usePageMeta({ title: 'Transfers' });

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return transfers.filter((t) => {
      const closed = t.status === 'received' || t.status === 'cancelled';
      if (tab === 'history' ? !closed : closed) return false;
      if (tab === 'out' && t.direction !== 'out') return false;
      if (tab === 'in' && t.direction !== 'in') return false;
      if (!q) return true;
      return `${t.number ?? ''} ${t.fromBranch ?? ''} ${t.toBranch ?? ''} ${t.lines
        .map((l) => l.itemName)
        .join(' ')}`
        .toLowerCase()
        .includes(q);
    });
  }, [transfers, tab, query]);

  const counts = useMemo(
    () => ({
      out: transfers.filter((t) => t.direction === 'out' && t.status !== 'received' && t.status !== 'cancelled').length,
      in: transfers.filter((t) => t.direction === 'in' && t.status === 'in_transit').length,
    }),
    [transfers],
  );

  const picked = transfers.find((t) => t.id === pickedId) ?? null;

  /* A CLINIC WITH ONE SITE HAS NOTHING TO BALANCE. Rather than an empty
     screen with a disabled button, it says so and points at what it is for. */
  const singleSite = (branches?.length ?? 0) < 2;


  /* THE PHONE'S COMPOSER (`g_k7`): the open draft, or a blank one that becomes a
     draft the moment the first item goes on it. */
  const draft = transfers.find((t) => t.direction === 'out' && t.status === 'draft') ?? null;
  const toBranch = draft?.toBranchId ?? (dest || elsewhere[0]?.id || '');
  const toName = (draft?.toBranch ?? elsewhere.find((b) => b.id === toBranch)?.name) ?? '';
  const startDraft = (then?: (t: Transfer) => void) => {
    if (draft) return then?.(draft);
    if (!toBranch) return;
    act.createDraft.mutate(
      { toBranchId: toBranch },
      {
        onSuccess: (t) => then?.(t),
        onError: () => toast.error('The transfer was not started.'),
      },
    );
  };

  /* `?add=<item>` — the shelf's "Add to transfer": the item lands on the draft. */
  const adding = useRef<string | null>(null);
  const addId = params.get('add');
  useEffect(() => {
    if (!addId || adding.current === addId || !canWrite || !here || !elsewhere.length || !shelf || isLoading) return;
    adding.current = addId;
    startDraft((t) => {
      act.addLine.mutate(
        { transferId: t.id, itemId: addId, quantity: 1 },
        { onError: (e: unknown) => toast.error(errText(e, 'It was not added.')) },
      );
      setPicked(t.id);
      const next = new URLSearchParams(params);
      next.delete('add');
      setParams(next, { replace: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addId, canWrite, here?.id, elsewhere.length, shelf, isLoading]);

  const tableRows: TableRow[] = rows.map((t) => ({
    id: t.id,
    cells: {
      transfer: (
        <div style={{ minWidth: 0 }}>
          <p className="t-strong">
            {t.number ? `#${t.number} ` : 'Draft '}
            {t.direction === 'in' ? `← ${t.fromBranch}` : `→ ${t.toBranch}`}
          </p>
          <p className="t-cap mut">
            {t.status === 'draft'
              ? t.lines.map((l) => `${l.itemName} ×${l.quantity}`).join(' · ') || 'nothing on it yet'
              : t.status === 'in_transit'
                ? t.direction === 'out'
                  ? 'sent · awaiting their confirmation'
                  : 'sitting here until you confirm it'
                : `confirmed at ${t.toBranch}`}
          </p>
        </div>
      ),
      items: <span className="t-strong">{t.items}</span>,
      route: (
        <span className="mut">
          {t.fromBranch} → {t.toBranch}
        </span>
      ),
      state: <StateChip transfer={t} />,
    },
  }));

  /* Every transfer but the open draft, whichever way it goes — the composer above is the draft. */
  const phoneTransferRows: TableRow[] = transfers
    .filter((t) => !(t.direction === 'out' && t.status === 'draft'))
    .map((t) => ({
      id: t.id,
      cells: {
        transfer: (
          <div style={{ minWidth: 0 }}>
            <p className="t-strong">
              {t.number ? `#${t.number} ` : ''}
              {t.direction === 'in' ? `← ${t.fromBranch}` : `→ ${t.toBranch}`}
            </p>
            <p className="t-cap mut">
              {t.items} item{t.items === 1 ? '' : 's'} ·{' '}
              {t.status === 'in_transit'
                ? t.direction === 'out' ? 'awaiting their confirmation' : 'sitting here until you confirm it'
                : `confirmed at ${t.toBranch}`}
            </p>
          </div>
        ),
        state: <StateChip transfer={t} />,
      },
    }));

  useShellChrome(
    {
      raw: true,
      /* Phone: the pick is its own screen; back clears it. */
      paneOpen: !!picked,
      onPaneClose: () => setPicked(null),
      paneMenu: (close) => <StockMenu door="transfers" onClose={close} />,
      /* `g_k7` — the desk table as cards. The rows are the table's
         own, so a figure or a state can never read differently here. */
      phone: (
        <PhoneScreen
          className="stock-phone"
          crumb="Stock"
          title="Transfer ⇄"
          onBack={() => go('/stock-manager/shelf')}
          menu={(close) => <StockMenu door="transfers" onClose={close} />}
        >
          {canWrite && !singleSite ? (
            <>
              <div className="card" style={{ borderColor: 'var(--brand-300)' }}>
                <PickerChip
                  label={`${here?.name ?? 'Here'} → ${toName || 'Pick a branch'} ▾`}
                  ariaLabel="Route"
                  kind="brand"
                  value={toBranch}
                  onChange={setDest}
                  options={elsewhere.map((b) => ({ value: b.id, label: `${here?.name ?? 'Here'} → ${b.name}` }))}
                />
              </div>
              {(draft?.lines ?? []).map((l) => (
                <div key={l.id} className="card">
                  <div className="flex" style={{ gap: 8, alignItems: 'center' }}>
                    <p className="t-strong grow">{l.itemName}</p>
                    <p className="t-strong">{l.quantity}</p>
                    <DChip onClick={() => act.removeLine.mutate(l.id)}>Take off</DChip>
                  </div>
                  <p className="t-cap mut mt4">
                    {l.lotCode ? `from batch ${l.lotCode}${l.expiresOn ? ` (exp ${expiryLabel(l.expiresOn)})` : ''} — earliest kept home` : 'no batches'}
                  </p>
                </div>
              ))}
              <p className="mt8 mb8">
                <button type="button" className="lnk w6 brand"
                  style={{ background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'pointer' }}
                  disabled={!toBranch || act.createDraft.isPending}
                  onClick={() => startDraft((t) => setComposing(t))}>
                  + Add item
                </button>
              </p>
              <button type="button" className="btn btn-primary" disabled={!draft?.lines.length || act.send.isPending}
                onClick={() => draft && act.send.mutate(draft.id, {
                  onSuccess: (t) => toast.success(`${t.number} sent — it is on neither shelf until ${draft.toBranch} confirms`),
                  onError: (e: unknown) => toast.error(errText(e, 'It was not sent.')),
                })}>
                Send transfer
              </button>
              <p className="t-cap mut mt12" style={{ textAlign: 'center' }}>
                the other branch’s stock manager confirms receipt
              </p>
            </>
          ) : singleSite ? (
            <EmptyState title="This clinic has one site" sub="A transfer moves stock between two of them." />
          ) : null}
          {phoneTransferRows.length ? (
            <>
              <p className="t-ovl mut" style={{ margin: '16px 0 6px' }}>In transit and past</p>
              <PhoneCards
                columns={[
                  { key: 'transfer', label: 'Transfer', width: 46 },
                  { key: 'state', label: 'State', width: 18 },
                ]}
                rows={phoneTransferRows}
                body="transfer"
                trailing="state"
                onOpen={(r) => setPicked(r.id)}
              />
            </>
          ) : null}
        </PhoneScreen>
      ),
      title: 'Transfers',
      sub: singleSite
        ? 'balance the branches — this clinic has one'
        : `balance the branches · ${here?.name ?? ''}`,
      search: 'Transfer number, branch or item',
      searchValue: query,
      onSearchChange: setQuery,
      ...(canWrite && !singleSite
        ? {
            primary: {
              label: '⇄ New transfer',
              disabled: act.createDraft.isPending,
              onClick: () => {
                const to = elsewhere[0];
                if (!to) return;
                act.createDraft.mutate(
                  { toBranchId: to.id },
                  {
                    onSuccess: (t) => {
                      setComposing(t);
                      setPicked(t.id);
                      setTab('out');
                    },
                    onError: () => toast.error('The transfer was not started.'),
                  },
                );
              },
            },
          }
        : {}),
      pane: picked ? (
        <TransferPane
          transfer={picked}
          canWrite={canWrite}
          onClose={() => setPicked(null)}
          onOpenItem={picked.lines[0] ? () => go(`/stock-manager/catalog?edit=${picked.lines[0].itemId}`) : undefined}
          onCompose={() => setComposing(picked)}
          onSend={() =>
            act.send.mutate(picked.id, {
              onSuccess: (t) =>
                toast.success(
                  `${t.number} sent — ${picked.fromBranch}’s shelf is down, ${picked.toBranch}’s goes up when they confirm`,
                ),
              onError: (e: unknown) => toast.error(errText(e, 'It was not sent.')),
            })
          }
          onConfirm={() => setConfirming(picked)}
          onDismiss={() =>
            act.dismiss.mutate(picked.id, {
              onSuccess: () => {
                toast.success('Draft dismissed — nothing left a shelf');
                setPicked(null);
              },
            })
          }
        />
      ) : (
        <Pane
          head={
            <PaneHead
              overline={tab === 'in' ? 'Incoming' : tab === 'out' ? 'Outgoing' : 'History'}
              title="Nothing picked"
              sub={PANE_HINT[tab]}
            />
          }
        >
          <div className="emptystate">
            <p className="t-sec">{PANE_BODY[tab]}</p>
          </div>
        </Pane>
      ),
    },
    [picked, tab, canWrite, query, singleSite, here?.name, act.createDraft.isPending, pickedId, transfers, toBranch, draft, elsewhere.length],
  );

  return (
    <div className="maie desk stock-root">
      <div className="dcontent scroll">
        {!singleSite ? (
          <TabBar
            label="Transfers"
            items={[
              { key: 'out' as const, label: counts.out ? `Outgoing · ${counts.out}` : 'Outgoing' },
              { key: 'in' as const, label: counts.in ? `Incoming · ${counts.in}` : 'Incoming' },
              { key: 'history' as const, label: 'History' },
            ]}
            active={tab}
            onSelect={(k) => {
              setTab(k);
              setPicked(null);
            }}
          />
        ) : null}

        {singleSite ? (
          <div className="emptystate">
            <p className="es-t">This clinic has one site</p>
            <p className="t-sec mut">
              A transfer moves stock between two of them — it beats an order when the other branch
              is holding a surplus of exactly what you are short of. Open a second site in System
              Settings and this screen starts working.
            </p>
          </div>
        ) : (
          <DataTable
            caption={`Transfers — ${tab}`}
            columns={[
              { key: 'transfer', label: 'Transfer', width: 46 },
              { key: 'items', label: 'Items', width: 12 },
              { key: 'route', label: 'Route', width: 24 },
              { key: 'state', label: 'State', width: 18 },
            ]}
            rows={tableRows}
            selectedId={pickedId}
            onSelect={(r) => setPicked(r.id === pickedId ? null : r.id)}
            empty={
              <div className="emptystate">
                {isLoading ? (
                  <p className="t-sec mut">Reading the transfers…</p>
                ) : isError ? (
                  <p className="t-sec mut">
                    The transfers did not come back. Nothing has been changed.
                  </p>
                ) : (
                  <>
                    <p className="es-t">{EMPTY_TITLE[tab]}</p>
                    <p className="t-sec mut">{EMPTY_BODY[tab]}</p>
                  </>
                )}
              </div>
            }
          />
        )}

        {!singleSite ? <p className="t-sec mut mt16">{FOOTNOTE[tab]}</p> : null}
      </div>

      <ComposerModal
        transfer={composing}
        branches={elsewhere}
        shelf={shelf ?? []}
        onClose={() => setComposing(null)}
        onAdd={(itemId, quantity) => {
          if (!composing) return;
          act.addLine.mutate(
            { transferId: composing.id, itemId, quantity },
            {
              onError: (e: unknown) => toast.error(errText(e, 'It was not added.')),
            },
          );
        }}
        onRemove={(lineId) => act.removeLine.mutate(lineId)}
        live={transfers.find((t) => t.id === composing?.id) ?? composing}
      />

      <ConfirmModal
        transfer={confirming}
        onClose={() => setConfirming(null)}
        onConfirm={(lines) => {
          if (!confirming) return;
          act.confirm.mutate(
            { transferId: confirming.id, lines },
            {
              onSuccess: (t) =>
                toast.success(
                  `${t.number} confirmed — ${t.toBranch}’s shelf is up by what arrived`,
                ),
              onError: (e: unknown) => toast.error(errText(e, 'It was not confirmed.')),
            },
          );
          setConfirming(null);
        }}
      />
    </div>
  );
}

const errText = (e: unknown, fallback: string): string =>
  (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
    ?.message ?? fallback;

const PANE_HINT: Record<Tab, string> = {
  out: 'pick one to see what is moving, and whether it has left',
  in: 'pick one to confirm what actually arrived',
  history: 'pick a closed transfer to read what it moved',
};

const PANE_BODY: Record<Tab, string> = {
  out: 'Nothing leaves a shelf until the transfer is sent. Until then it is a list of intentions.',
  in: 'An incoming transfer sits here until you confirm it line by line. Nothing touches your shelf before that.',
  history:
    'A confirmed transfer is closed on both sides and cannot be edited — a correction is a new transfer, or a count variance with its note.',
};

const EMPTY_TITLE: Record<Tab, string> = {
  out: 'Nothing is going out',
  in: 'Nothing is coming in',
  history: 'No transfer has closed yet',
};

const EMPTY_BODY: Record<Tab, string> = {
  out: 'A transfer beats an order when the other branch is holding a surplus of exactly what you are short of.',
  in: 'When another branch sends you stock it waits here, on neither shelf, until you confirm what arrived.',
  history: 'Transfers land here once the receiving branch has confirmed them.',
};

const FOOTNOTE: Record<Tab, string> = {
  out: 'In transit until the RECEIVING branch’s stock manager confirms.',
  in: 'An incoming transfer sits here until you confirm it line by line. Nothing touches your shelf before that.',
  history:
    'A confirmed transfer is closed on both sides and cannot be edited — a correction is a new transfer, or a count variance with its note.',
};

function StateChip({ transfer }: { transfer: Transfer }) {
  if (transfer.status === 'draft') return <Chip kind="soft" small>draft</Chip>;
  if (transfer.status === 'in_transit') {
    return (
      <Chip kind="warn" small>
        {transfer.direction === 'in' ? 'confirm receipt' : 'in transit'}
      </Chip>
    );
  }
  if (transfer.status === 'received') return <Chip kind="success" small>confirmed ✓</Chip>;
  return <Chip kind="outline" small>dismissed</Chip>;
}

/** The docked transfer, read from whichever end you are standing at. */
function TransferPane({
  transfer,
  canWrite,
  onClose,
  onOpenItem,
  onCompose,
  onSend,
  onConfirm,
  onDismiss,
}: {
  transfer: Transfer;
  canWrite: boolean;
  onClose: () => void;
  /** `g_tr_hist` — a closed transfer's foot opens its item in the catalog. */
  onOpenItem?: () => void;
  onCompose: () => void;
  onSend: () => void;
  onConfirm: () => void;
  onDismiss: () => void;
}) {
  const incoming = transfer.direction === 'in';
  const overline =
    transfer.status === 'draft'
      ? 'Outgoing · draft'
      : transfer.status === 'in_transit'
        ? incoming
          ? 'Incoming · confirm receipt'
          : 'Outgoing · in transit'
        : 'Closed on both sides';
  const short = transfer.lines.filter((l) => l.shortfall > 0);

  return (
    <Pane
      onClose={onClose}
      head={
        <PaneHead
          overline={overline}
          title={
            transfer.number
              ? `#${transfer.number} ${incoming ? '←' : '→'} ${incoming ? transfer.fromBranch : transfer.toBranch}`
              : `${transfer.fromBranch} → ${transfer.toBranch}`
          }
          sub={
            transfer.status === 'draft'
              ? `${transfer.items} line${transfer.items === 1 ? '' : 's'} · not sent`
              : transfer.status === 'in_transit'
                ? `sent ${dayMonth(transfer.sentAt)}${transfer.sentBy ? ` by ${transfer.sentBy}` : ''}`
                : `confirmed ${dayMonth(transfer.receivedAt)}${short.length ? ` · ${short.length} line short` : ''}`
          }
          wrapTitle
        />
      }
      foot={
        transfer.status === 'received' && onOpenItem ? (
          <button type="button" className="btn btn-outline grow" style={{ minHeight: 44 }} onClick={onOpenItem}>
            Open the item ▸
          </button>
        ) : canWrite ? (
          transfer.status === 'draft' ? (
            <>
              <button type="button" className="btn btn-outline" style={{ flex: 1, minHeight: 44 }} onClick={onDismiss}>
                ✕ Dismiss
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{ flex: 1, minHeight: 44 }}
                disabled={!transfer.items}
                onClick={onSend}
              >
                Send transfer
              </button>
            </>
          ) : transfer.status === 'in_transit' && incoming ? (
            <button type="button" className="btn btn-primary grow" style={{ minHeight: 44 }} onClick={onConfirm}>
              ✓ Confirm receipt
            </button>
          ) : undefined
        ) : undefined
      }
    >
      {transfer.status === 'received' ? (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <div className="card" style={{ flex: 1, margin: 0 }}>
            <p className="t-ovl mut">Items</p>
            <p className="t-heading mt8">{transfer.items}</p>
          </div>
          <div className="card" style={{ flex: 1, margin: 0 }}>
            <p className="t-ovl mut">Route</p>
            <p className="t-heading mt8">{incoming ? `← ${transfer.fromBranch}` : `→ ${transfer.toBranch}`}</p>
          </div>
          <div className="card" style={{ flex: 1, margin: 0 }}>
            <p className="t-ovl mut">State</p>
            <p className="t-heading mt8">closed</p>
          </div>
        </div>
      ) : null}
      {transfer.status === 'received' && transfer.receivedBy ? (
        <div className="card" style={{ marginBottom: 12 }}>
          <p className="t-ovl mut">Confirmed by</p>
          <p className="t-strong mt4">{transfer.receivedBy} · {transfer.toBranch}</p>
          <p className="t-cap mut mt4">the receiving branch’s stock manager is the only person who can close it</p>
        </div>
      ) : null}
      <p className="t-ovl mut" style={{ margin: '4px 0 6px' }}>
        {transfer.status === 'received' ? 'What arrived' : `Receiving at ${transfer.toBranch}`}
      </p>

      {transfer.lines.length === 0 ? (
        <Card>
          <p className="t-sec mut">Nothing on it yet — a transfer with no lines moves nothing.</p>
        </Card>
      ) : (
        transfer.lines.map((line) => (
          <Card
            key={line.id}
            style={line.shortfall > 0 ? { borderColor: 'var(--danger-600)' } : undefined}
          >
            <div className="flex" style={{ gap: 8, alignItems: 'flex-start' }}>
              <p className="t-ovl mut grow">{line.itemName}</p>
              {line.shortfall > 0 ? (
                <p className="t-strong red">−{line.shortfall}</p>
              ) : transfer.status === 'received' ? (
                <p className="t-strong grn">✓</p>
              ) : null}
            </div>
            <p className="t-strong mt8">
              {transfer.status === 'received'
                ? `${line.receivedQuantity} of ${line.quantity} received`
                : `× ${line.quantity}`}
            </p>
            <p className="t-cap mut mt8">
              {line.lotCode
                ? `from batch ${line.lotCode}${line.expiresOn ? ` · exp ${expiryLabel(line.expiresOn)}` : ''} — the earliest is kept home`
                : `${line.unit || 'item'} · no batches — nothing to choose`}
            </p>
            {line.note ? <p className="t-sec red mt4">{line.note}</p> : null}
          </Card>
        ))
      )}

      {transfer.status === 'draft' ? (
        <>
          {canWrite ? (
            <p className="mt8">
              <DChip kind="brand" onClick={onCompose}>+ Add item</DChip>
            </p>
          ) : null}
          <Card kind="accent">
            <p className="t-sec brand">Later-expiry batches travel first by default.</p>
          </Card>
          <Card>
            <p className="t-ovl mut">Nothing has moved yet</p>
            <p className="t-strong mt8">
              {transfer.fromBranch} still owns all{' '}
              {transfer.lines.reduce((sum, l) => sum + l.quantity, 0)} units
            </p>
            <p className="t-cap mut mt8">the shelf changes only when the transfer is sent</p>
          </Card>
        </>
      ) : null}

      {transfer.status === 'in_transit' ? (
        <Card style={{ borderColor: 'var(--warning-600)' }}>
          <p className="t-ovl mut">Sent by</p>
          <p className="t-strong mt8">
            {transfer.fromBranch} · {dayMonth(transfer.sentAt)}
          </p>
          <p className="t-cap mut mt8">
            {incoming
              ? 'their shelf went down when they sent it — yours goes up when you confirm'
              : 'your shelf is already down; theirs goes up when they confirm'}
          </p>
        </Card>
      ) : null}

      {transfer.status === 'in_transit' && incoming ? (
        <Card kind="accent">
          <p className="t-sec brand">
            Batches travel with their expiry; the sender’s FEFO stays honest.
          </p>
        </Card>
      ) : null}

      {short.length ? (
        <Card kind="accent">
          <p className="t-sec brand">
            The shortfall is on the record at both branches — a difference neither shelf absorbed.
          </p>
        </Card>
      ) : null}
    </Pane>
  );
}

/** Putting boxes on a draft. The shelf's own numbers decide what can go. */
function ComposerModal({
  transfer,
  live,
  branches,
  shelf,
  onClose,
  onAdd,
  onRemove,
}: {
  transfer: Transfer | null;
  live: Transfer | null;
  branches: Array<{ id: string; name: string }>;
  shelf: ShelfItem[];
  onClose: () => void;
  onAdd: (itemId: string, quantity: number) => void;
  onRemove: (lineId: string) => void;
}) {
  const [itemId, setItemId] = useState('');
  const [qty, setQty] = useState('');
  const item = shelf.find((i) => i.id === itemId) ?? null;
  const asked = Number(qty) || 0;
  const ready = !!item && asked > 0 && asked <= (item?.inStock ?? 0);

  return (
    <Modal
      open={!!transfer}
      onClose={onClose}
      title={transfer ? `${transfer.fromBranch} → ${transfer.toBranch}` : 'Transfer'}
      sub={branches.length > 1 ? 'the receiving branch is set when the draft is raised' : undefined}
      foot={<button type="button" className="btn btn-primary" onClick={onClose}>Done</button>}
    >
      {live?.lines.length ? (
        <>
          <p className="t-ovl mut">On this transfer</p>
          {live.lines.map((l) => (
            <Card key={l.id}>
              <div className="flex" style={{ gap: 8, alignItems: 'center' }}>
                <div className="grow" style={{ minWidth: 0 }}>
                  <p className="t-strong">
                    {l.itemName} × {l.quantity}
                  </p>
                  <p className="t-cap mut">
                    {l.lotCode ? `batch ${l.lotCode}${l.expiresOn ? ` · exp ${expiryLabel(l.expiresOn)}` : ''}` : 'no batches'}
                  </p>
                </div>
                <DChip kind="warn" onClick={() => onRemove(l.id)}>Take off</DChip>
              </div>
            </Card>
          ))}
        </>
      ) : null}

      <p className="t-ovl mut mt12">Add an item</p>
      <p className="mt4">
        <PickerChip
          label={`${item?.name ?? 'Which item'} ▾`}
          ariaLabel="Item"
          kind={itemId ? 'brand' : 'outline'}
          value={itemId}
          onChange={setItemId}
          options={[
            { value: '', label: 'Pick one' },
            ...shelf
              .filter((i) => i.inStock > 0)
              .map((i) => ({
                value: i.id,
                label: i.name,
                hint: `${i.inStock} on the shelf`,
              })),
          ]}
        />
      </p>
      {item ? (
        <>
          <label className="t-ovl mut mt12" htmlFor="tr-qty">How many</label>
          <input
            id="tr-qty"
            className="fld t-strong mt4 num"
            type="number"
            min={1}
            max={item.inStock}
            inputMode="numeric"
            placeholder={`up to ${item.inStock}`}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
          <p className="t-cap mut mt8">
            {item.inStock} on this shelf
            {item.minLevel != null ? ` against a minimum of ${item.minLevel}` : ''}
            {item.batchTracked && item.lots.length > 1
              ? ' · the longest-dated batch travels, the earliest is kept home'
              : ''}
          </p>
          {asked > item.inStock ? (
            <p className="t-sec red mt8">
              Only {item.inStock} here — this branch cannot send {asked}.
            </p>
          ) : null}
          <p className="mt12">
            <DChip
              kind="brand"
              onClick={() => {
                if (!ready) return;
                onAdd(item.id, asked);
                setItemId('');
                setQty('');
              }}
            >
              + Put it on the transfer
            </DChip>
          </p>
        </>
      ) : null}
    </Modal>
  );
}

/** Confirming receipt: what actually arrived, line by line. */
function ConfirmModal({
  transfer,
  onClose,
  onConfirm,
}: {
  transfer: Transfer | null;
  onClose: () => void;
  onConfirm: (lines: Array<{ lineId: string; quantity: number; note?: string }>) => void;
}) {
  const [got, setGot] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});

  const lines = transfer?.lines ?? [];
  const valueOf = (l: TransferLine) =>
    got[l.id] === undefined || got[l.id] === '' ? l.quantity : Number(got[l.id]);
  const shortLines = lines.filter((l) => valueOf(l) < l.quantity);
  /* SHORT NEEDS A NOTE — the server refuses without one, so the button does
     too rather than letting it round-trip into an error. */
  const ready = shortLines.every((l) => (notes[l.id] ?? '').trim().length > 2);

  return (
    <Modal
      open={!!transfer}
      onClose={onClose}
      title={transfer ? `Confirm #${transfer.number}` : 'Confirm receipt'}
      sub={transfer ? `from ${transfer.fromBranch} — say what actually arrived` : undefined}
      foot={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Not yet</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!ready}
            onClick={() =>
              onConfirm(
                lines.map((l) => ({
                  lineId: l.id,
                  quantity: valueOf(l),
                  ...(notes[l.id]?.trim() ? { note: notes[l.id].trim() } : {}),
                })),
              )
            }
          >
            ✓ Confirm receipt
          </button>
        </>
      }
    >
      {lines.map((l) => {
        const v = valueOf(l);
        const isShort = v < l.quantity;
        return (
          <Card key={l.id} style={isShort ? { borderColor: 'var(--danger-600)' } : undefined}>
            <div className="flex" style={{ gap: 8, alignItems: 'center' }}>
              <p className={`t-sec grow${isShort ? ' red' : ''}`}>
                {l.itemName} — sent {l.quantity}
              </p>
              <input
                className={`fld t-strong num${isShort ? ' red' : ' grn'}`}
                style={{ width: 64, textAlign: 'end' }}
                type="number"
                min={0}
                max={l.quantity}
                inputMode="numeric"
                aria-label={`How many ${l.itemName} arrived`}
                placeholder={String(l.quantity)}
                value={got[l.id] ?? ''}
                onChange={(e) => setGot((g) => ({ ...g, [l.id]: e.target.value }))}
              />
            </div>
            {isShort ? (
              <>
                <p className="t-sec mt8" style={{ color: 'var(--danger-600)' }}>
                  Short {l.quantity - v} — a note is required, and it is logged on BOTH sides.
                </p>
                <input
                  className="fld t-strong mt4"
                  aria-label={`Why ${l.itemName} is short`}
                  placeholder="what happened to them"
                  value={notes[l.id] ?? ''}
                  onChange={(e) => setNotes((n) => ({ ...n, [l.id]: e.target.value }))}
                />
              </>
            ) : null}
          </Card>
        );
      })}
      <p className="t-cap mut mt12">
        A line you leave alone is confirmed as sent. Only what you confirm lands on your shelf, and
        the batches keep their dates — so FEFO here sorts by the label, not by the delivery day.
      </p>
    </Modal>
  );
}
